import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Footer.css';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState<{
    title?: string;
    description?: string;
    benefits?: string[];
  }>({});
  const [sparkPosition, setSparkPosition] = useState(0);
  const [hormoneLevels, setHormoneLevels] = useState({
    estrogen: 60,
    progesterone: 70,
    testosterone: 40,
    thyroid: 55,
    cortisol: 75
  });
  
  const location = useLocation();

  // Auto scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Animate the electric spark effect
  useEffect(() => {
    const sparkInterval = setInterval(() => {
      setSparkPosition(prev => (prev >= 100 ? 0 : prev + 5));
    }, 150);
    
    return () => clearInterval(sparkInterval);
  }, []);

  // Update hormone levels with random fluctuations
  useEffect(() => {
    const hormoneFluct = setInterval(() => {
      setHormoneLevels({
        estrogen: 60 + Math.sin(Date.now() / 2000) * 20,
        progesterone: 70 + Math.sin(Date.now() / 1800 + 1) * 15,
        testosterone: 40 + Math.sin(Date.now() / 2200 + 2) * 10,
        thyroid: 55 + Math.sin(Date.now() / 1900 + 3) * 18,
        cortisol: 75 + Math.sin(Date.now() / 2100 + 4) * 12
      });
    }, 100);
    
    return () => clearInterval(hormoneFluct);
  }, []);

  // Custom link component that scrolls to top
  const ScrollToTopLink = useCallback(({ 
    to, 
    className, 
    children, 
    onMouseEnter 
  }: { 
    to: string; 
    className?: string; 
    children: React.ReactNode; 
    onMouseEnter?: (e: React.MouseEvent) => void;
  }) => {
    const handleClick = () => {
      window.scrollTo(0, 0);
    };

    return (
      <Link 
        to={to} 
        className={className} 
        onClick={handleClick} 
        onMouseEnter={onMouseEnter}
      >
        {children}
      </Link>
    );
  }, []);

  // Hide tooltip when mouse leaves
  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  // Interactive service info handler
  const handleServiceHover = useCallback((service: string, e: React.MouseEvent) => {
    const serviceInfo: Record<string, {
      title: string;
      description: string;
      benefits: string[];
    }> = {
      'estrogen': {
        title: 'Bioidentical Estrogen Therapy',
        description: 'Natural estrogen therapy to relieve menopause symptoms and improve overall wellbeing.',
        benefits: ['Reduces hot flashes', 'Improves sleep quality', 'Supports bone health', 'Enhances mood']
      },
      'progesterone': {
        title: 'Bioidentical Progesterone Therapy',
        description: 'Balance hormones naturally to reduce PMS, improve sleep, and enhance fertility.',
        benefits: ['Regulates menstrual cycles', 'Supports sleep', 'Reduces anxiety', 'Balances estrogen']
      },
      'testosterone': {
        title: 'Women\'s Testosterone Therapy',
        description: 'Low-dose testosterone to improve energy, libido, and cognitive function.',
        benefits: ['Increases energy', 'Enhances libido', 'Improves muscle tone', 'Supports mental clarity']
      },
      'thyroid': {
        title: 'Thyroid Optimization',
        description: 'Comprehensive thyroid care to resolve fatigue, weight issues, and mood disorders.',
        benefits: ['Increases metabolism', 'Improves energy', 'Supports weight management', 'Enhances mood']
      },
      'dhea': {
        title: 'DHEA & Adrenal Support',
        description: 'Support for your body\'s stress response system and hormone production.',
        benefits: ['Reduces fatigue', 'Supports immune function', 'Improves stress response', 'Enhances vitality']
      },
      'wellness': {
        title: 'Integrative Wellness Plans',
        description: 'Personalized health plans combining hormone therapy with nutrition and lifestyle.',
        benefits: ['Comprehensive approach', 'Personalized care', 'Preventative focus', 'Long-term solutions']
      }
    };

    if (serviceInfo[service]) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({ 
        x: rect.left, 
        y: rect.top - 220 // Position above the element
      });
      setTooltipContent(serviceInfo[service]);
      setShowTooltip(true);
    }
  }, []);

  // Custom hormone indicator renderer
  const renderHormoneIndicator = useCallback((
    name: string, 
    level: number | undefined, 
    barClass: string
  ) => {
    const height = level !== undefined 
      ? `${level}%` 
      : '0%';
    
    return (
      <div className="hormone-column">
        <div 
          className={`hormone-bar ${barClass}`} 
          style={{ height }}
        />
        <span className="hormone-label">{name}</span>
      </div>
    );
  }, []);

  return (
    <div className="footer-wrapper">
      {/* Interactive Service Tooltip */}
      {showTooltip && (
        <div 
          className="service-tooltip"
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            zIndex: 1000
          }}
          onMouseLeave={handleMouseLeave}
          role="tooltip"
          aria-hidden="false"
        >
          <h4>{tooltipContent.title}</h4>
          <p>{tooltipContent.description}</p>
          {tooltipContent.benefits && tooltipContent.benefits.length > 0 && (
            <ul>
              {tooltipContent.benefits.map((benefit: string, index: number) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Call-to-Action Banner */}
      <section className="cta-banner" aria-label="Call to action">
        <div className="cta-container">
          <div className="cta-content">
            <span className="cta-tagline">Take the first step toward balance</span>
            <h3 className="cta-headline">Restore your hormonal health today</h3>
          </div>
          <div className="cta-buttons">
            <ScrollToTopLink to="/booking" className="cta-button cta-primary">
              <span className="icon-calendar" aria-hidden="true"></span>
              Schedule Consultation
            </ScrollToTopLink>
            <ScrollToTopLink to="/contact" className="cta-button cta-secondary">
              <span className="icon-phone" aria-hidden="true"></span>
              Contact Us
            </ScrollToTopLink>
          </div>
        </div>
      </section>

      {/* Main Footer */}
      <footer className="footer">
        {/* Wave Separator */}
        <svg className="footer-wave" viewBox="0 0 1200 120" preserveAspectRatio="none" aria-hidden="true">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff"></path>
        </svg>
        
        {/* Subtle Texture Overlay */}
        <div className="footer-texture" aria-hidden="true"></div>
        
        <div className="footer-main">
          {/* Footer Top Section */}
          <div className="footer-top">
            <div className="footer-container">
              {/* Top Information Section */}
              <div className="footer-top-info">
                {/* Brand Column */}
                <div className="footer-brand">
                  <img src={require('../assets/logo.png')} alt="VitalBalanceHormones" className="footer-logo" width="220" height="60" />
                  <h3 className="footer-tagline">Restore Balance, Reclaim Vitality</h3>
                  <p className="footer-brand-text">
                    At VitalBalanceHormones, we're dedicated to helping women restore hormonal balance through personalized bioidentical hormone therapy, comprehensive testing, and holistic wellness approaches that address the root causes of your symptoms.
                  </p>
                </div>
                
                {/* Contact Information */}
                <div className="footer-contact-box">
                  <h4 className="footer-column-title">Contact Information</h4>
                  <div className="contact-grid">
                    <div className="contact-grid-item">
                      <div className="contact-icon" aria-hidden="true">
                        📍
                      </div>
                      <div className="contact-text">
                        <span className="contact-label">Address</span>
                        <address className="contact-detail">Littleton, CO 80123</address>
                      </div>
                    </div>
                    <div className="contact-grid-item">
                      <div className="contact-icon" aria-hidden="true">
                        📞
                      </div>
                      <div className="contact-text">
                        <span className="contact-label">Phone</span>
                        <a href="tel:7207735733" className="contact-detail">(720) 773-5733</a>
                      </div>
                    </div>
                    <div className="contact-grid-item">
                      <div className="contact-icon" aria-hidden="true">
                        ✉️
                      </div>
                      <div className="contact-text">
                        <span className="contact-label">Email</span>
                        <a href="mailto:info@vitalbalancehormones.com" className="contact-detail">info@vitalbalancehormones.com</a>
                      </div>
                    </div>
                    <div className="contact-grid-item">
                      <div className="contact-icon" aria-hidden="true">
                        🕒
                      </div>
                      <div className="contact-text">
                        <span className="contact-label">Hours</span>
                        <span className="contact-detail">Mon-Fri: 9am-5pm, Sat: 10am-2pm</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="footer-social">
                    <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon instagram" aria-label="Instagram">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                    <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon facebook" aria-label="Facebook">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                      </svg>
                    </a>
                    <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer" className="social-icon tiktok" aria-label="TikTok">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                      </svg>
                    </a>
                    <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="social-icon linkedin" aria-label="LinkedIn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/>
                      </svg>
                    </a>
                    <a href="https://www.youtube.com" target="_blank" rel="noopener noreferrer" className="social-icon youtube" aria-label="YouTube">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Two Column Section */}
              <div className="footer-nav-columns">
                {/* Site Navigation */}
                <div className="footer-column">
                  <h4 className="footer-column-title">Site Navigation</h4>
                  <nav aria-label="Footer Navigation">
                    <ul className="footer-links">
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/" className="footer-link">Home</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/about" className="footer-link">About Us</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/services" className="footer-link">Services</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/blog" className="footer-link">Wellness Blog</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/booking" className="footer-link">Book Consultation</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/contact" className="footer-link">Contact Us</ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink to="/faq" className="footer-link">FAQs</ScrollToTopLink>
                      </li>
                    </ul>
                  </nav>
                </div>
                
                {/* Services - Interactive Version */}
                <div className="footer-column">
                  <h4 className="footer-column-title">Our Services</h4>
                  <nav aria-label="Services Navigation">
                    <ul className="footer-links interactive-services">
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/estrogen" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('estrogen', e)}
                        >
                          Bioidentical Estrogen Therapy
                        </ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/progesterone" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('progesterone', e)}
                        >
                          Bioidentical Progesterone Therapy
                        </ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/testosterone" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('testosterone', e)}
                        >
                          Women's Testosterone Therapy
                        </ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/thyroid" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('thyroid', e)}
                        >
                          Thyroid Optimization
                        </ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/dhea" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('dhea', e)}
                        >
                          DHEA & Adrenal Support
                        </ScrollToTopLink>
                      </li>
                      <li className="footer-link-item">
                        <ScrollToTopLink 
                          to="/services/wellness" 
                          className="footer-link"
                          onMouseEnter={(e) => handleServiceHover('wellness', e)}
                        >
                          Integrative Wellness Plans
                        </ScrollToTopLink>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              
              {/* Full-width hormone monitor */}
              <div className="hormone-monitor-full" aria-label="Hormone Balance Monitor">
                <div className="hormone-monitor-header">
                  <h4 className="hormone-monitor-title">
                    <span className="monitor-icon" aria-hidden="true">⚡</span>
                    Hormone Balance Monitor
                  </h4>
                  <div className="live-indicator">
                    <div className="pulse-dot" aria-hidden="true"></div>
                    <span>LIVE HORMONE TRACKING</span>
                  </div>
                </div>
                
                <div className="hormone-visualizer-full" aria-label="Interactive hormone level visualization">
                  {/* Hormone Level Indicators */}
                  <div className="hormone-indicators">
                    {renderHormoneIndicator("Estrogen", hormoneLevels.estrogen, "estrogen-bar")}
                    {renderHormoneIndicator("Progesterone", hormoneLevels.progesterone, "progesterone-bar")}
                    {renderHormoneIndicator("Testosterone", hormoneLevels.testosterone, "testosterone-bar")}
                    {renderHormoneIndicator("Thyroid", hormoneLevels.thyroid, "thyroid-bar")}
                    {renderHormoneIndicator("Cortisol", hormoneLevels.cortisol, "cortisol-bar")}
                    {renderHormoneIndicator("DHEA", 70 + Math.sin(Date.now() / 2300 + 5) * 15, "dhea-bar")}
                    {renderHormoneIndicator("Insulin", 50 + Math.sin(Date.now() / 2400 + 6) * 20, "insulin-bar")}
                    {renderHormoneIndicator("Melatonin", 35 + Math.sin(Date.now() / 2500 + 7) * 25, "melatonin-bar")}
                  </div>
                  
                  {/* Grid Lines */}
                  <div className="monitor-grid" aria-hidden="true"></div>
                  
                  {/* Y-axis labels */}
                  <div className="monitor-y-axis" aria-hidden="true">
                    <div className="y-axis-label">High</div>
                    <div className="y-axis-label">Normal</div>
                    <div className="y-axis-label">Low</div>
                  </div>
                  
                  {/* Status indicators */}
                  <div className="status-indicators" aria-hidden="true">
                    <div className="status-item">
                      <div className="status-dot optimal"></div>
                      <span>Optimal</span>
                    </div>
                    <div className="status-item">
                      <div className="status-dot high"></div>
                      <span>High</span>
                    </div>
                    <div className="status-item">
                      <div className="status-dot low"></div>
                      <span>Low</span>
                    </div>
                  </div>
                </div>
                
                <div className="hormone-details">
                  <div className="badges-container">
                    <div className="badge">
                      <span className="icon-award" aria-hidden="true"></span>
                      <span>AACE Certified</span>
                    </div>
                    <div className="badge">
                      <span className="icon-shield" aria-hidden="true"></span>
                      <span>HIPAA Compliant</span>
                    </div>
                    <div className="badge">
                      <span className="icon-star" aria-hidden="true"></span>
                      <span>Top Rated {currentYear}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer Bottom */}
          <div className="footer-bottom">
            <div className="footer-container">
              <div className="footer-bottom-container">
                <div className="copyright">
                  &copy; {currentYear} VitalBalanceHormones. All Rights Reserved.
                </div>
                <nav aria-label="Legal Navigation">
                  <div className="footer-nav">
                    <ScrollToTopLink to="/privacy-policy" className="footer-nav-link">Privacy Policy</ScrollToTopLink>
                    <ScrollToTopLink to="/terms-of-service" className="footer-nav-link">Terms of Service</ScrollToTopLink>
                    <ScrollToTopLink to="/sitemap" className="footer-nav-link">Sitemap</ScrollToTopLink>
                    <ScrollToTopLink to="/accessibility" className="footer-nav-link">Accessibility</ScrollToTopLink>
                    <a href="https://medspa.com" target="_blank" rel="noopener noreferrer" className="footer-nav-link">MedSpa Website</a>
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Enhanced Gaazzeebo Subfooter with Electric Spark Effect */}
      <div className="gaazzeebo-subfooter">
        <div className="gaazzeebo-bg" aria-hidden="true"></div>
        
        {/* Electric Spark Animation Track */}
        <div className="spark-track" aria-hidden="true">
          <div className="spark-bolt" style={{ left: `${sparkPosition}%` }}></div>
          
          {/* Lightning branches that randomly appear */}
          {sparkPosition % 15 === 0 && (
            <div className="spark-branch spark-branch-top"></div>
          )}
          
          {sparkPosition % 20 === 0 && (
            <div className="spark-branch spark-branch-bottom"></div>
          )}
        </div>
        
        <a 
          href="https://gaazzeebo.net" 
          target="_blank" 
          rel="noopener noreferrer" 
          className="gaazzeebo-link"
          aria-label="Powered by GAAZZEEBO - Visit Gaazzeebo.net"
        >
          <span className="powered-by">POWERED BY</span>
          <span className="gaazzeebo-text"> GAAZZEEBO</span>
        </a>
      </div>
    </div>
  );
};

export default Footer;